import { FirebaseApp, initializeApp } from "firebase/app";
import { getMessaging, isSupported, Messaging } from "firebase/messaging";

import { Secret } from "../models/secret";

let firebaseApp: FirebaseApp | null = null;
let messaging: Messaging | null = null;

export interface FirebaseSecretData {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
}

export const initializeFirebase = async (firebaseSecretData: FirebaseSecretData): Promise<Messaging | null> => {
  try {
    const messagingSupported = await isSupported();
    if (!messagingSupported) {
      console.warn("Firebase Messaging is not supported in this browser");
      return null;
    }

    if (firebaseApp && messaging) {
      return messaging;
    }

    if ("serviceWorker" in navigator) {
      try {
        const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
        console.log("Service Worker registered successfully:", registration);
        
        if (registration.installing) {
          await new Promise<void>((resolve) => {
            registration.installing!.addEventListener("statechange", (e) => {
              if ((e.target as ServiceWorker).state === "activated") {
                resolve();
              }
            });
          });
        } else if (registration.waiting) {
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
          await new Promise<void>((resolve) => {
            navigator.serviceWorker.addEventListener("controllerchange", () => {
              resolve();
            }, { once: true });
          });
        }
        
        await navigator.serviceWorker.ready;
      } catch (error) {
        console.error("Service Worker registration failed:", error);
        return null;
      }
    } else {
      console.warn("Service Workers are not supported in this browser");
      return null;
    }

    firebaseApp = initializeApp(firebaseSecretData);
    messaging = getMessaging(firebaseApp);

    return messaging;
  } catch (error) {
    console.error("Error initializing Firebase:", error);
    return null;
  }
};

/**
 * Get the current Firebase Messaging instance
 * @returns Firebase Messaging instance or null
 */
export const getMessagingInstance = (): Messaging | null => {
  return messaging;
};

/**
 * Get the VAPID key from Secret Manager
 * @param secret - Secret object containing VAPID key
 * @returns VAPID key string or null
 */
export const getVapidKey = (secret: Secret): string | null => {
  return secret.FIREBASE_VAPID_KEY || null;
};

